import { isPlatformBrowser, NgClass } from '@angular/common';
import type { AfterViewInit, ElementRef, OnDestroy, OnInit } from '@angular/core';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    inject,
    Input,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';

import { AbstractWebsiteComponent } from 'components/molecules/abstract-website-component/abstract-website-component';
import { SearchInputComponent } from 'components/molecules/search-input/search-input.component';
import type { MenuItem, SidebarSetting } from 'components/organisms/sidebar/entities/types';
import { MenuItemComponent } from 'components/organisms/sidebar/partial/menu-item/menu-item.component';
import { MenuItemsListComponent } from 'components/organisms/sidebar/partial/menu-items-list/menu-items-list.component';
import { SidebarService } from 'components/organisms/sidebar/sidebar.service';
import { LocaleService } from 'src/services/locale/locale.service';
import { StickyService } from 'src/services/sticky/sticky.service';
import { WindowResizeService } from 'src/services/window-resize/window-resize.service';
import { INNER_WIDTH_LAPTOP } from 'src/shared/constants/breakpoints/breakpoints';

@Component({
    selector: 'website-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SearchInputComponent, NgClass, TranslatePipe, MenuItemsListComponent, MenuItemComponent],
})
export class SidebarComponent extends AbstractWebsiteComponent implements OnInit, AfterViewInit, OnDestroy {
    public readonly COMPONENT_NAME = 'sidebar';

    public readonly sidebarService = inject(SidebarService);
    public readonly localeService = inject(LocaleService);
    private readonly document = inject(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    public readonly stickyService = inject(StickyService);
    private readonly windowResizeService = inject(WindowResizeService);

    @Input() sidebarSetting: SidebarSetting;
    @ViewChild('scrollContent') private scrollContent: ElementRef;

    public isTablet: boolean;
    public menuItem: MenuItem | null = null;
    private readonly translateService = inject(TranslateService);
    private destroy$ = new Subject<void>();

    protected readonly changeDetectionRef = inject(ChangeDetectorRef);

    ngOnInit() {
        this.menuItem = {
            title: this.translateService.instant('Guide overview'),
            url: '',
            isAnchor: false,
        };

        if (isPlatformBrowser(this.platformId)) {
            this.initIsTablet();
            this.setupResizeListener();
            this.setupScrollListener();
        }
    }

    ngAfterViewInit() {
        this.setScrollContentHeight();

        if (this.isTablet) {
            this.sidebarService.initMobileHeight();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupScrollListener(): void {
        fromEvent(window, 'scroll', { passive: true })
            .pipe(throttleTime(50), takeUntil(this.destroy$))
            .subscribe(() => {
                this.checkSidebarHeaderIntersection();
            });
    }

    private checkSidebarHeaderIntersection(): void {
        const headerShadowElement = this.document.querySelector<HTMLDivElement>('.js-header__shadow');

        if (this.isTablet) {
            const sidebarParentContainer = this.document.getElementById('sidebar-menu');
            const SCROLL_TRASH_HOLD = 50;

            sidebarParentContainer.classList.toggle('border-active', window.pageYOffset > SCROLL_TRASH_HOLD);
            headerShadowElement.style.display = 'none';
        } else {
            headerShadowElement.style.display = 'block';
        }

        this.changeDetectionRef.detectChanges();
    }

    private setupResizeListener(): void {
        this.windowResizeService.resize$.pipe(throttleTime(100), takeUntil(this.destroy$)).subscribe(() => {
            this.sidebarService.removeOverflow();
            this.initIsTablet();
            this.setScrollContentHeight();

            this.isTablet && this.sidebarService.initMobileHeight();
        });
    }

    private setScrollContentHeight(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        if (this.scrollContent?.nativeElement) {
            const viewportHeight = window.innerHeight;
            const targetHeight = Math.floor(viewportHeight * 0.85); // 85% from viewport

            this.scrollContent.nativeElement.style.height = `${targetHeight}px`;

            if (this.isTablet) {
                this.scrollContent.nativeElement.style.removeProperty('height');
            }

            this.changeDetectionRef.detectChanges();
        }
    }

    private initIsTablet() {
        this.isTablet = window.innerWidth <= INNER_WIDTH_LAPTOP;
    }
}
